import { useState } from "react";
import Card from "../../UI/Card";
import Callout from "../../UI/Callout";
import Editor from "../../RichTextEditor/Editor";
import Button from "../../UI/Button";
import Tabs from "../../UI/Tabs";
import ModifChargeInputs from "./CourriersFields/ModifChargeInputs";
import ModifLoyerInputs from "./CourriersFields/ModifLoyerInputs";
import ProrogationMobilite from "./CourriersFields/ProrogationMobilite";
import ChangementLocataire from "./CourriersFields/ChangementLocataire";
import { isCourrierWithFields } from "../../../models/courrier";
import CongeLegitime from "./CourriersFields/CongeLegitime";
import CongeReprise from "./CourriersFields/CongeReprise";
import CongeVenteNu from "./CourriersFields/CongeVenteNu";
import CourrierTrouble from "./CourriersFields/CourrierTrouble";
import RegulForm from "./RegulForm";
import RevisionForm from "./RevisionForm";
import { BalanceType } from "../../../utils/constants/balances";
import ConvocationEdlForm from "./ConvocationEdlForm";
import RestitutionDGForm from "./CourriersFields/RestitutionDGForm";
import CourrierTFToemForm from "./CourriersFields/CourrierTFToemForm";
import { useFormikContext } from "formik";
import AvisEcheanceForm from "./CourriersFields/AvisEcheanceForm";
import FormUpload from "../../File/FormUpload";
import DocumentDownloadLink from "../../UI/DocumentDownloadLink";
import DocumentThumbnail from "../../UI/DocumentThumbnail";
import { DocType } from "../../../utils/constants/documents";
import { ModalDialogBody, ModalDialogFooter } from "../../UI/ModalDialog";
import fieldStyles from "./CourrierFields.module.css";

export default function CourrierFields({
  courrierId,
  tenant,
  content,
  previewPdfUrl,
  isSubmitting,
  profiles,
  onContentChange,
  onPreview,
  onSelectProfile,
  onCreateProfile,
  balances,
  property,
  isAvenant,
  bailFile,
  error,
}) {
  const { selectNextTab, selectPreviousTab } = Tabs.useActiveTab();
  const [editorKey, setEditorKey] = useState(0);
  const { values } = useFormikContext();

  const hasFields = isCourrierWithFields(courrierId);
  const hasOwnHeader = courrierId === "regul_charge";
  const isAvisEcheance = courrierId === "avis_echeance";
  const hasDocument = isAvisEcheance
    ? Boolean(previewPdfUrl)
    : Boolean(content);

  async function handlePreview() {
    await onPreview();
    setEditorKey((k) => k + 1);
  }

  const isFactureAllHaveFacture =
    courrierId === "avis_echeance" &&
    tenant &&
    !tenant.is_type_quittance &&
    values.lignes?.length > 0 &&
    values.lignes.every((l) => l.has_facture);
  return (
    <>
      <ModalDialogBody className={fieldStyles.body}>
        {!hasOwnHeader && hasFields && (
          <p className={"primaryText text-center marginB10"}>
            Veuillez compléter les champs suivants
          </p>
        )}
        {!hasFields && (
          <p className={"primaryText text-center marginB10"}>
            Vous pouvez modifier le document selon vos besoins. Cliquez sur
            "suivant" lorsque vous êtes prêts.
          </p>
        )}
        {courrierId === "modif_charge" && (
          <ModifChargeInputs typeCharge={tenant?.type_charge} />
        )}
        {courrierId === "modif_loyer" && <ModifLoyerInputs />}
        {courrierId === "prorogation_mobilite" && <ProrogationMobilite />}
        {courrierId === "conge_legitime" && <CongeLegitime />}
        {courrierId === "conge_vente_nu" && <CongeVenteNu />}
        {(courrierId === "conge_reprise_nu" ||
          courrierId === "conge_reprise_meuble") && <CongeReprise />}
        {(courrierId === "courrier_trouble" ||
          courrierId === "courrier_med_trouble") && <CourrierTrouble />}
        {courrierId === "changement_locataire" && (
          <ChangementLocataire
            profiles={profiles}
            onSelectProfile={onSelectProfile}
            onCreateProfile={onCreateProfile}
          />
        )}
        {courrierId === "regul_charge" && (
          <RegulForm
            balances={balances.filter(
              (filtered) => filtered.type === BalanceType.APPEL_CHARGE,
            )}
            property={property}
            tenant={tenant}
          />
        )}
        {courrierId === "courrier_irl" && (
          <RevisionForm tenant={tenant} property={property} />
        )}
        {courrierId === "convocation_edl" && <ConvocationEdlForm />}
        {courrierId === "restitution_dg" && (
          <RestitutionDGForm property={property} tenant={tenant} />
        )}
        {(courrierId === "courrier_toem" || courrierId === "courrier_tf") && (
          <CourrierTFToemForm property={property} courrierId={courrierId} />
        )}
        {courrierId === "avis_echeance" && <AvisEcheanceForm tenant={tenant} />}

        {isAvenant && (
          <Card className={"marginB10 padding10"}>
            <p className={"primaryText marginB10"}>
              Le bail sera automatiquement annexé à l'avenant :
            </p>
            {bailFile ? (
              <div className={"flex gap10 align-items-center"}>
                <DocumentDownloadLink
                  documentId={bailFile.id}
                  doctype={DocType.GENERIC_FILE}
                  file={bailFile}
                >
                  <DocumentThumbnail
                    documentId={bailFile.id}
                    doctype={DocType.GENERIC_FILE}
                    file={bailFile}
                  />
                </DocumentDownloadLink>
                <span className={"secondaryText"}>{bailFile.filename}</span>
              </div>
            ) : (
              <Callout variant="warning">
                Aucun bail trouvé pour ce locataire
              </Callout>
            )}
          </Card>
        )}

        <FormUpload tag="autre" warning label={"Justificatifs à joindre"} />

        {hasDocument && (
          <>
            {hasFields && !isAvisEcheance && (
              <Callout variant="warning">
                Ne pas modifier les champs surlignés en jaune, utilisez les
                champs du formulaire ci-dessus. Autrement Qalimo ne pourra pas
                mettre sa base de donnée à jour avec ces nouvelles informations.
              </Callout>
            )}
            {isAvisEcheance ? (
              <iframe
                src={previewPdfUrl}
                title="Aperçu du document"
                className={fieldStyles.facturePreview}
              />
            ) : (
              <Editor
                key={editorKey}
                onChange={onContentChange}
                defaultValue={content}
              />
            )}
          </>
        )}
      </ModalDialogBody>
      <div className={fieldStyles.footerError}>
        <Callout variant="danger">{error}</Callout>
      </div>
      <ModalDialogFooter
        start={
          <Button type="button" variant="secondary" onClick={selectPreviousTab}>
            Précédent
          </Button>
        }
      >
        <Button
          type="button"
          variant={hasDocument ? "secondary" : "primary"}
          onClick={handlePreview}
          isLoading={isSubmitting}
          disabled={isSubmitting || isFactureAllHaveFacture}
        >
          {hasDocument ? "Regénérer le document" : "Générer le document"}
        </Button>
        {hasDocument && (
          <Button type="button" onClick={selectNextTab}>
            Suivant
          </Button>
        )}
      </ModalDialogFooter>
    </>
  );
}
